// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import { combineEngineMetricResults, labelEngineMetricResult } from "./utils.js";
import { patternWorkPhaseName } from "../core/config.js";
import {
  formatCompactElapsedTime,
} from "../core/format.js";
import { extentFromValues } from "../core/math.js";
import { cylinderKeys, cylinderNumberFromKey } from "../core/schema.js";
import {
  engineCylinderKeys,
  projectFlightDataForEngine,
} from "../core/engines.js";
import {
  clampScore,
  groundContactState,
  mostlyGroundContact,
  lowPowerEngineWorkPoint as engineWorkPoint,
  median,
  rollingMedianStep,
} from "./utils.js";
import {
  magCheckParameters,
} from "./detectors/mag-check.js";

function medianForWindow(points, key) {
  return median(points.map((point) => point[key]).filter(Number.isFinite));
}

function cylinderWindowMedian(points, prefix, cylinder) {
  return medianForWindow(points, `${prefix}${cylinder}`);
}

function pointGroundSpeed(point) {
  return ["groundSpeed", "ias", "tas"].map((key) => point[key]).find(Number.isFinite) ?? null;
}

function mostlyGroundWindow(points) {
  const config = magCheckParameters.groundWindow;
  const contact = mostlyGroundContact(points, config.minGroundShare);
  if (contact !== null) return contact;
  const speeds = points.map(pointGroundSpeed).filter(Number.isFinite);
  return !speeds.length || speeds.filter((speed) => speed <= config.maxGroundSpeedKt).length / speeds.length >= config.minGroundShare;
}

function sustainedGroundContactAirborneIndex(points, startIndex = 0) {
  const config = magCheckParameters.preTakeoff;
  const minSamples = config.minGroundContactAirborneSamples ?? 3;
  let streak = 0;
  for (let index = Math.max(0, startIndex); index < points.length; index += 1) {
    const state = groundContactState(points[index]);
    if (state === false) {
      streak += 1;
      if (streak >= minSamples) return index - streak + 1;
    } else {
      streak = 0;
    }
  }
  return -1;
}

function rangeOf(values) {
  const extent = extentFromValues(values);
  return extent ? extent.max - extent.min : null;
}

function rangeForWindow(points, key) {
  const extent = extentFromValues(points.map((point) => point[key]));
  return extent ? extent.max - extent.min : null;
}

function baselineEgtUpwardDrift(baseline, egtKeys) {
  if (baseline.length < 4) return 0;
  const mid = Math.floor(baseline.length / 2);
  const firstHalf = baseline.slice(0, mid);
  const secondHalf = baseline.slice(mid);
  let maxDrift = 0;
  for (const key of egtKeys) {
    const firstVals = firstHalf.map((point) => point[key]).filter(Number.isFinite);
    const secondVals = secondHalf.map((point) => point[key]).filter(Number.isFinite);
    if (firstVals.length < 2 || secondVals.length < 2) continue;
    const drift = (secondVals.reduce((sum, value) => sum + value, 0) / secondVals.length)
      - (firstVals.reduce((sum, value) => sum + value, 0) / firstVals.length);
    if (drift > maxDrift) maxDrift = drift;
  }
  return maxDrift;
}

function hasSingleMagRunupContext(baseline, segment, baselineRpm, segmentRpm) {
  const config = magCheckParameters.runupContext;
  if (!mostlyGroundWindow([...baseline, ...segment])) return false;
  const runupLabeled = [...baseline, ...segment].some((point) => point.phase === "Runup" || point.phase === "Ground Run");
  const minimumBaselineRpm = runupLabeled ? config.labeledMinimumBaselineRpm : config.unlabeledMinimumBaselineRpm;
  if (!Number.isFinite(baselineRpm) || baselineRpm < minimumBaselineRpm) return false;
  if (!Number.isFinite(segmentRpm) || segmentRpm < config.minimumSegmentRpm) return false;
  return true;
}

export function candidateMagPhase(points) {
  const counts = new Map();
  points.forEach((point) => {
    if (!point.phase) return;
    counts.set(point.phase, (counts.get(point.phase) ?? 0) + 1);
  });
  let best = "Unknown";
  let bestCount = 0;
  counts.forEach((count, phase) => {
    if (count <= bestCount) return;
    best = phase;
    bestCount = count;
  });
  return best;
}

function magCandidatePhaseLabel(points, baselineRpm, segmentRpm) {
  const config = magCheckParameters.runupContext;
  if (points.some((point) => point.phase === "Runup")) return "Runup";
  if (points.some((point) => point.phase === "Ground Run")) return "Ground Run";
  const phase = candidateMagPhase(points);
  if (["Startup", "Landing", "Shutdown"].includes(phase)) return phase;
  if ((Number.isFinite(baselineRpm) && baselineRpm >= config.groundRunupBaselineRpm)
    || (Number.isFinite(segmentRpm) && segmentRpm >= config.groundRunupSegmentRpm)) {
    return "ground runup";
  }
  return phase;
}

function magSegmentQuality(candidate) {
  const config = magCheckParameters.quality.runupBlock;
  let quality = 100;
  if (!candidate.onGround) quality -= config.offGroundPenalty;
  if (candidate.duration < config.minimumDurationSeconds || candidate.duration > config.maximumDurationSeconds) quality -= config.durationPenalty;
  if ((candidate.rpmRange ?? 0) < config.minimumRpmRange) quality -= config.rpmRangePenalty;
  if (candidate.medianRise < config.minimumMedianRiseF) quality -= config.medianRisePenalty;
  return clampScore(quality);
}

function magCoverage(points, key, step) {
  const config = magCheckParameters.sequence;
  const valid = points.filter((point) => Number.isFinite(point[key]));
  const maxGap = Math.max(config.minimumGapSeconds, step * config.maximumGapSteps);
  const continuous = valid.every((point, index) => !index || point.elapsedSeconds - valid[index - 1].elapsedSeconds <= maxGap);
  return {
    samples: valid.length,
    share: points.length ? valid.length / points.length : 0,
    usable: valid.length >= config.minimumSamples && valid.length / points.length >= config.minimumCoverageShare && continuous,
  };
}

function magSettledPoints(points) {
  if (!points.length) return [];
  const config = magCheckParameters.sequence;
  const duration = points.at(-1).elapsedSeconds - points[0].elapsedSeconds;
  const after = points[0].elapsedSeconds + Math.min(config.settlingSeconds, duration * config.settlingShare);
  return points.filter((point) => point.elapsedSeconds >= after);
}

// RPM boundaries are shared with operating-context annotations. EGT evidence is
// evaluated only inside these intervals, never in a window straddling the onset.
export function magCheckRpmSegments(input) {
  const data = preTakeoffMagWindowData(input);
  const config = magCheckParameters.sequence;
  const step = rollingMedianStep(data);
  const maxGap = Math.max(config.minimumGapSeconds, step * config.maximumGapSteps);
  const segments = [];
  let floor = 0;
  for (let start = 1; start < data.length; start += 1) {
    const time = data[start].elapsedSeconds;
    if (!Number.isFinite(time) || !Number.isFinite(data[start].rpm)) continue;
    while (floor < start && data[floor].elapsedSeconds < time - Math.max(magCheckParameters.search.baselineWindowSeconds, config.minimumSamples * step)) floor += 1;
    const baseline = data.slice(floor, start);
    const baselineRpm = medianForWindow(baseline, "rpm");
    const drop = baselineRpm - data[start].rpm;
    if (!Number.isFinite(baselineRpm) || drop < magCheckParameters.singleDrop.minimumRpmDrop) continue;
    if (drop > magCheckParameters.singleDrop.maximumRpmDrop) continue;
    if (!magCoverage(baseline, "rpm", step).usable) continue;
    if (baseline.at(-1).elapsedSeconds - baseline[0].elapsedSeconds < config.minimumBaselineSeconds) continue;
    if (time - baseline.at(-1).elapsedSeconds > maxGap) continue;
    if (!hasSingleMagRunupContext(baseline, [data[start]], baselineRpm, data[start].rpm)) continue;
    if ((rangeForWindow(baseline, "rpm") ?? 0) > magCheckParameters.quality.singleDrop.baselineRpmRangeLimit) continue;
    let stop = start + 1;
    while (stop < data.length) {
      if (data[stop].elapsedSeconds - data[stop - 1].elapsedSeconds > maxGap) break;
      if (Number.isFinite(data[stop].rpm) && data[stop].rpm >= baselineRpm - magCheckParameters.dipCounting.exitRpmDrop) break;
      stop += 1;
    }
    const segment = data.slice(start, stop);
    const recovery = [];
    for (let index = stop; index < data.length; index += 1) {
      const point = data[index];
      if (point.elapsedSeconds - data[index - 1].elapsedSeconds > maxGap) break;
      if (point.elapsedSeconds - data[stop].elapsedSeconds > config.recoverySeconds) break;
      if (!Number.isFinite(point.rpm) || point.rpm < baselineRpm - magCheckParameters.dipCounting.exitRpmDrop
        || point.rpm > baselineRpm + magCheckParameters.singleDrop.maximumSegmentRpmOvershoot) break;
      recovery.push(point);
    }
    const duration = segment.at(-1).elapsedSeconds - time;
    if (duration >= config.minimumCheckSeconds && mostlyGroundWindow(segment)) {
      segments.push({ start, end: stop - 1, baseline, segment, recovery, baselineRpm, step });
    }
    if (stop < data.length) floor = stop;
    start = Math.max(start, stop - 1);
  }
  return segments;
}

function summarizeMagCandidate(raw, egtKeys) {
  const config = magCheckParameters.singleDrop;
  const sequence = magCheckParameters.sequence;
  const { start, end, baseline, segment, recovery, baselineRpm, step } = raw;
  if (!segment.every(engineWorkPoint)) return null;
  if (baselineEgtUpwardDrift(baseline, egtKeys) > config.maximumBaselineEgtDriftF) return null;
  const segmentRpm = medianForWindow(segment, "rpm");
  if (!hasSingleMagRunupContext(baseline, segment, baselineRpm, segmentRpm)) return null;
  const settled = magSettledPoints(segment);
  const recovered = magSettledPoints(recovery);
  const confidenceReasons = [];
  const inconclusiveReasons = [];
  const rpmRecovered = magCoverage(recovery, "rpm", step).usable;
  if (!rpmRecovered) inconclusiveReasons.push("RPM recovery was not fully recorded.");
  if (!magCoverage(segment, "rpm", step).usable) inconclusiveReasons.push("Too few continuous RPM readings during the check.");
  const duration = segment.at(-1).elapsedSeconds - segment[0].elapsedSeconds;
  if (duration > sequence.maximumCheckSeconds) inconclusiveReasons.push("The RPM drop lasts too long to identify a discrete check.");
  const cylinderResponses = egtKeys.map((key) => {
    const cylinder = cylinderNumberFromKey(key);
    const coverage = {
      baseline: magCoverage(baseline, key, step),
      check: magCoverage(settled, key, step),
      recovery: magCoverage(recovered, key, step),
    };
    const usable = coverage.baseline.usable && coverage.check.usable;
    const before = coverage.baseline.usable ? medianForWindow(baseline, key) : null;
    const during = coverage.check.usable ? medianForWindow(settled, key) : null;
    const rise = usable ? during - before : null;
    const after = coverage.recovery.usable ? medianForWindow(recovered, key) : null;
    const recoveryObserved = usable && Number.isFinite(after);
    const egtRecovered = recoveryObserved && Math.abs(after - before) <= Math.max(sequence.recoveryToleranceF, Math.abs(rise) * sequence.recoveryToleranceShare);
    if (!usable) inconclusiveReasons.push(`EGT${cylinder} has insufficient readings inside the baseline or check.`);
    else if (!recoveryObserved) inconclusiveReasons.push(`EGT${cylinder} recovery has insufficient readings.`);
    else if (!egtRecovered) inconclusiveReasons.push(`EGT${cylinder} did not show a clear return toward baseline.`);
    if (usable && coverage.check.samples < sequence.highConfidenceSamples) confidenceReasons.push(`EGT${cylinder} has limited samples during the check.`);
    return { cylinder, key, before, during, rise, after, coverage, available: usable, egtRecovered, segmentRange: usable ? rangeForWindow(settled, key) : null };
  });
  const availableResponses = cylinderResponses.filter((item) => item.available);
  const medianRise = median(availableResponses.map((item) => item.rise));
  const peerRiseSpread = rangeOf(availableResponses.map((item) => item.rise)) ?? 0;
  if (availableResponses.length < Math.max(config.minimumCylinderCount, Math.min(config.targetCylinderCount, egtKeys.length))) {
    inconclusiveReasons.push("Too few cylinders have usable EGT response coverage.");
  }
  if (!Number.isFinite(medianRise) || medianRise < config.minimumMedianRiseF) inconclusiveReasons.push("The recorded EGT response does not clearly corroborate a mag check.");
  const weakResponses = availableResponses.filter((item) => item.rise < Math.max(config.weakMinimumRiseF, medianRise * config.weakRiseRatio)
    || item.rise < config.weakNegativeRiseF).sort((a, b) => a.rise - b.rise);
  const unstableResponses = availableResponses.filter((item) => item.segmentRange > sequence.unstableEgtRangeF);
  const candidate = {
    mode: "singleMagDrop", start, end,
    startTime: segment[0].elapsedSeconds, endTime: segment.at(-1).elapsedSeconds, duration,
    phase: magCandidatePhaseLabel([...baseline, ...segment], baselineRpm, segmentRpm),
    baselineRpm, segmentRpm, rpmDrop: baselineRpm - segmentRpm,
    rpmMin: Math.min(...segment.map((point) => point.rpm).filter(Number.isFinite)),
    baselineRpmRange: rangeForWindow(baseline, "rpm") ?? 0,
    segmentRpmRange: rangeForWindow(segment, "rpm") ?? 0,
    baselineWindow: { startTime: baseline[0].elapsedSeconds, endTime: baseline.at(-1).elapsedSeconds },
    recovery: { startTime: recovery[0]?.elapsedSeconds ?? null, endTime: recovery.at(-1)?.elapsedSeconds ?? null, rpmRecovered,
      egtRecovered: cylinderResponses.length > 0 && cylinderResponses.every((item) => item.egtRecovered) },
    medianRise, peerRiseSpread, cylinderResponses, weakResponses, unstableResponses,
    dipCount: 1, confidenceReasons, inconclusiveReasons,
  };
  let quality = 100;
  for (const [key, prefix, limit, label] of [["fuelFlow", "fuel", magCheckParameters.quality.singleDrop.fuelChangeLimitGph, "Fuel flow"],
    ["map", "map", magCheckParameters.quality.singleDrop.mapChangeLimitInHg, "MAP"]]) {
    const covered = magCoverage(baseline, key, step).usable && magCoverage(segment, key, step).usable;
    const before = covered ? medianForWindow(baseline, key) : null;
    const during = covered ? medianForWindow(segment, key) : null;
    const change = covered ? Math.abs(during - before) : null;
    candidate[`${prefix}Before`] = before;
    candidate[`${prefix}During`] = during;
    candidate[`${prefix}Change`] = change;
    if (!covered) { confidenceReasons.push(`${label} stability could not be checked.`); quality -= sequence.missingContextPenalty; }
    else if (change > limit || (rangeForWindow(segment, key) ?? 0) > limit || (rangeForWindow(baseline, key) ?? 0) > limit) {
      inconclusiveReasons.push(`${label} moved during the check or its baseline.`);
    }
  }
  if (candidate.segmentRpmRange > magCheckParameters.quality.singleDrop.segmentRpmRangeLimit) inconclusiveReasons.push("RPM was unstable during the check.");
  candidate.quality = inconclusiveReasons.length ? Math.min(quality, magCheckParameters.scoring.inconclusiveQualityThreshold - 1) : quality;
  candidate.confidence = inconclusiveReasons.length ? "Low" : confidenceReasons.length ? "Medium" : "High";
  candidate.confidenceReasons = [...inconclusiveReasons, ...confidenceReasons];
  candidate.suspicion = weakResponses.length
    ? config.suspicionBase + Math.max(0, peerRiseSpread - config.spreadSuspicionFloorF) * config.spreadSuspicionWeight
      + Math.max(0, medianRise - weakResponses[0].rise) * config.weakGapSuspicionWeight
    : unstableResponses.length ? config.suspicionBase : 0;
  candidate.responseStatus = inconclusiveReasons.length ? "inconclusive" : candidate.suspicion > 0 ? "review" : "consistent";
  return candidate;
}

function summarizeRunupMagCandidate(data, start, egtKeys) {
  const config = magCheckParameters.runupBlock;
  const baselineCount = config.baselineSamples;
  const lookahead = config.lookaheadSamples;
  const baseline = data.slice(Math.max(0, start - baselineCount), start);
  if (baseline.length < config.minBaselineSamples) return null;
  const baselineRpm = medianForWindow(baseline, "rpm");
  if (!Number.isFinite(baselineRpm) || baselineRpm < config.minBaselineRpm) return null;
  const post = data.slice(start, Math.min(data.length, start + lookahead));
  if (post.length < config.minPostSamples) return null;
  const eventStartOffset = post.findIndex((point) => Number.isFinite(point.rpm) && point.rpm >= baselineRpm + config.eventRpmRise);
  if (eventStartOffset < 0) return null;
  const eventPost = post.slice(eventStartOffset);
  const segmentEndOffset = eventPost.findIndex((point, index) => index > config.segmentEndMinIndex && Number.isFinite(point.rpm) && point.rpm < baselineRpm + config.segmentEndRpmRise);
  const segment = segmentEndOffset > config.minSegmentSamples ? eventPost.slice(0, segmentEndOffset) : eventPost;
  if (segment.length < config.minSegmentSamples || !mostlyGroundWindow(segment)) return null;
  const segmentRpm = medianForWindow(segment, "rpm");
  const rpmIncrease = segmentRpm - baselineRpm;
  const rpmRange = rangeForWindow(segment, "rpm") ?? 0;
  if (!Number.isFinite(segmentRpm) || rpmIncrease < config.minRpmIncrease || segmentRpm < config.minSegmentRpm) return null;
  const cylinderResponses = egtKeys.map((key) => {
    const cylinder = cylinderNumberFromKey(key);
    const before = cylinderWindowMedian(baseline, "egt", cylinder);
    const during = cylinderWindowMedian(segment.slice(Math.max(config.egtTailMinimumSamples, Math.floor(segment.length * config.egtTailShare))), "egt", cylinder);
    const rise = Number.isFinite(before) && Number.isFinite(during) ? during - before : null;
    const segmentRange = rangeForWindow(segment, key);
    return { cylinder, key, before, during, rise, segmentRange };
  }).filter((item) => Number.isFinite(item.rise));
  if (cylinderResponses.length < Math.max(config.minimumCylinderCount, Math.min(config.targetCylinderCount, egtKeys.length))) return null;
  const rises = cylinderResponses.map((item) => item.rise);
  const medianRise = median(rises) ?? 0;
  if (medianRise < config.minimumMedianRiseF) return null;
  const peerRiseSpread = rangeOf(rises) ?? 0;
  const weakResponses = [];
  const fuelBefore = medianForWindow(baseline, "fuelFlow");
  const fuelDuring = medianForWindow(segment, "fuelFlow");
  const mapBefore = medianForWindow(baseline, "map");
  const mapDuring = medianForWindow(segment, "map");
  const duration = (segment.at(-1)?.elapsedSeconds ?? start + segment.length) - (segment[0]?.elapsedSeconds ?? start);
  const candidate = {
    mode: "runupBlock",
    start: start + eventStartOffset,
    end: start + eventStartOffset + segment.length - 1,
    startTime: segment[0]?.elapsedSeconds ?? start,
    endTime: segment.at(-1)?.elapsedSeconds ?? start + segment.length - 1,
    duration,
    phase: "Runup",
    onGround: mostlyGroundWindow(segment),
    baselineRpm,
    segmentRpm,
    rpmIncrease,
    rpmDrop: null,
    rpmRange,
    baselineRpmRange: rangeForWindow(baseline, "rpm") ?? 0,
    segmentRpmRange: rpmRange,
    fuelBefore,
    fuelDuring,
    fuelChange: Number.isFinite(fuelBefore) && Number.isFinite(fuelDuring) ? Math.abs(fuelDuring - fuelBefore) : null,
    mapBefore,
    mapDuring,
    mapChange: Number.isFinite(mapBefore) && Number.isFinite(mapDuring) ? Math.abs(mapDuring - mapBefore) : null,
    medianRise,
    peerRiseSpread,
    cylinderResponses,
    weakResponses,
  };
  candidate.quality = magSegmentQuality(candidate);
  candidate.suspicion = 0;
  return candidate;
}

function findMagCheckCandidates(data) {
  const config = magCheckParameters.search;
  const egtKeys = cylinderKeys("egt").filter((key) => data.some((point) => Number.isFinite(point[key])));
  if (egtKeys.length < 2 || !data.some((point) => Number.isFinite(point.rpm))) return [];
  const step = rollingMedianStep(data);
  const baselineCount = Math.max(config.minSamples, Math.round(config.baselineWindowSeconds / step));
  const candidates = magCheckRpmSegments(data).map((raw) => summarizeMagCandidate(raw, egtKeys)).filter(Boolean);
  if (candidates.length) return candidates;
  const maxRunupStart = Math.min(data.length, Math.round(config.maxRunupSearchSeconds / Math.max(step, config.minStepSeconds)));
  for (let start = baselineCount; start < maxRunupStart; start += Math.max(config.minStrideSamples, Math.round(config.runupScanSeconds / step))) {
    const previous = medianForWindow(data.slice(Math.max(0, start - config.previousRpmSamples), start), "rpm");
    const next = medianForWindow(data.slice(start, Math.min(data.length, start + config.nextRpmSamples)), "rpm");
    if (!Number.isFinite(previous) || !Number.isFinite(next) || next - previous < config.runupRpmJump) continue;
    const candidate = summarizeRunupMagCandidate(data, start, egtKeys);
    if (candidate) candidates.push(candidate);
  }
  return candidates.sort((a, b) => b.quality - a.quality || a.startTime - b.startTime);
}

function preTakeoffMagWindowData(data) {
  const config = magCheckParameters.preTakeoff;
  const takeoffPhases = new Set(["Takeoff", "Climb", patternWorkPhaseName, "Cruise", "Descent", "Landing"]);
  const takeoffIndex = data.findIndex((point) => takeoffPhases.has(point.phase));
  if (takeoffIndex === 0) return [];
  if (takeoffIndex > 0) return data.slice(0, takeoffIndex);

  const first = data[0];
  const contactAirborneIndex = sustainedGroundContactAirborneIndex(
    data,
    data.findIndex((point) => (point.elapsedSeconds ?? 0) >= config.minMotionElapsedSeconds),
  );
  if (contactAirborneIndex > 0) return data.slice(0, contactAirborneIndex);

  const groundAltitude = median(data.slice(0, Math.min(data.length, config.initialAltitudeSamples)).map((point) => point.altitude).filter(Number.isFinite));
  const motionIndex = data.findIndex((point) => {
    const elapsed = point.elapsedSeconds ?? 0;
    if (elapsed < config.minMotionElapsedSeconds) return false;
    const speed = pointGroundSpeed(point);
    const altitudeGain = Number.isFinite(point.altitude) && Number.isFinite(groundAltitude)
      ? point.altitude - groundAltitude
      : 0;
    return (Number.isFinite(speed) && speed >= config.minGroundSpeedKt)
      || (Number.isFinite(point.verticalSpeed) && point.verticalSpeed >= config.minVerticalSpeedFpm)
      || altitudeGain >= config.minAltitudeGainFt;
  });
  if (motionIndex > 0) return data.slice(0, motionIndex);

  const firstElapsed = first?.elapsedSeconds ?? 0;
  const fallbackEnd = firstElapsed + config.fallbackSeconds;
  return data.filter((point) => (point.elapsedSeconds ?? 0) <= fallbackEnd);
}

function magWindowLabel(candidate) {
  if (!candidate || !Number.isFinite(candidate.startTime)) return "";
  const start = formatCompactElapsedTime(candidate.startTime);
  const end = Number.isFinite(candidate.endTime) ? formatCompactElapsedTime(candidate.endTime) : "";
  return end ? `${start}-${end}` : start;
}

function analyzeMagCheckForKeys(data, egtSourceKeys) {
  const scoring = magCheckParameters.scoring;
  const egtKeys = egtSourceKeys.filter((key) => data.some((point) => Number.isFinite(point[key])));
  if (egtKeys.length < 2) {
    return { key: "mag", score: null, note: "Need multiple EGT channels", details: {} };
  }
  if (!data.some((point) => Number.isFinite(point.rpm))) {
    return { key: "mag", score: null, note: "Need RPM channel for mag-check screen", details: {} };
  }
  const preTakeoffData = preTakeoffMagWindowData(data);
  if (preTakeoffData.length < magCheckParameters.preTakeoff.minSamples) {
    return {
      key: "mag",
      score: null,
      note: "No pre-takeoff mag check detected",
      details: { candidates: 0, preTakeoffSamples: preTakeoffData.length },
    };
  }
  const candidates = findMagCheckCandidates(preTakeoffData);
  if (!candidates.length) {
    return {
      key: "mag",
      score: null,
      note: "No pre-takeoff mag check detected",
      details: { candidates: 0, preTakeoffSamples: preTakeoffData.length },
    };
  }
  const sequences = [];
  for (const candidate of candidates) {
    const previous = sequences.at(-1)?.checks.at(-1);
    if (!previous || !previous.recovery?.rpmRecovered
      || candidate.startTime - previous.endTime > magCheckParameters.sequence.maximumSeparationSeconds) {
      sequences.push({ checks: [] });
    }
    sequences.at(-1).checks.push(candidate);
  }
  for (const [index, sequence] of sequences.entries()) {
    sequence.startTime = sequence.checks[0].startTime;
    sequence.endTime = sequence.checks.at(-1).endTime;
    sequence.checkCount = sequence.checks.length;
    sequence.recoveredCheckCount = sequence.checks.filter((check) => check.recovery?.rpmRecovered && check.recovery?.egtRecovered).length;
    sequence.status = sequence.recoveredCheckCount < sequence.checkCount ? "incomplete" : sequence.checkCount < 2 ? "single-check" : "complete";
    for (const [checkIndex, check] of sequence.checks.entries()) {
      check.sequenceIndex = index;
      check.checkIndex = checkIndex;
      check.checkLabel = ["First check", "Second check"][checkIndex] ?? `Check ${checkIndex + 1}`;
      check.dipCount = sequence.checkCount;
      check.score = check.mode === "runupBlock" || check.responseStatus === "inconclusive" ? null
        : check.suspicion > 0 ? clampScore(100 - Math.min(scoring.suspicionCap, check.suspicion))
          : clampScore(scoring.cleanScore - (sequence.checkCount === 1 ? scoring.singleDipPenalty : 0));
    }
  }
  const review = candidates.filter((check) => Number.isFinite(check.score) && check.score < scoring.reviewScoreThreshold);
  const top = review.sort((a, b) => a.score - b.score)[0]
    ?? candidates.find((check) => !Number.isFinite(check.score)) ?? candidates[0];
  const sequence = sequences[top.sequenceIndex];
  const confidence = candidates.some((check) => check.confidence === "Low" || check.mode === "runupBlock") ? "Low"
    : candidates.some((check) => check.confidence === "Medium") ? "Medium" : "High";
  const details = { top, candidates, events: candidates, sequences, confidence,
    confidenceReasons: [...new Set(candidates.flatMap((check) => check.confidenceReasons ?? []))],
    completeness: { status: sequence.status, checkCount: sequence.checkCount, recoveredCheckCount: sequence.recoveredCheckCount } };
  if (!Number.isFinite(top.score)) return { key: "mag", score: null, note: "Mag-check-like segment inconclusive", details };
  const weak = top.weakResponses[0];
  const at = magWindowLabel(top);
  const cylCount = top.cylinderResponses.filter((item) => item.available).length;
  const suffix = sequence.checkCount === 1 ? ", single dip" : `, ${sequence.checkCount} checks`;
  const note = weak ? `Possible mag check at ${at}, weak EGT response on cyl ${weak.cylinder}`
    : top.unstableResponses.length ? `Possible mag check at ${at}, unstable EGT response on cyl ${top.unstableResponses[0].cylinder}`
      : `Possible mag check at ${at}, RPM drop ${Math.round(top.rpmDrop)}, EGT rise ${Math.round(top.medianRise)} F, ${cylCount} cyl${suffix}`;
  return { key: "mag", score: top.score, note, details };
}

export function magCheckOperatingEvents(data, options = {}) {
  if (options.engineDescriptors?.length > 1) {
    return options.engineDescriptors.flatMap((engine) => magCheckOperatingEvents(projectFlightDataForEngine(data, engine))
      .map((event) => ({ ...event, engineKey: engine.engineKey, engineLabel: engine.label })));
  }
  const result = analyzeMagCheckForKeys(data, cylinderKeys("egt"));
  const candidates = Array.isArray(result.details.candidates) ? result.details.candidates : [];
  if (!candidates.length && /Need .*channels?/.test(result.note)) {
    return magCheckRpmSegments(data).map(({ baselineRpm, segment }) => ({
      eventType: "mag-check", startTime: segment[0].elapsedSeconds, endTime: segment.at(-1).elapsedSeconds,
      durationSeconds: segment.at(-1).elapsedSeconds - segment[0].elapsedSeconds, phase: "Runup", severity: "info",
      rpmBaseline: baselineRpm, rpmMin: Math.min(...segment.map((point) => point.rpm).filter(Number.isFinite)),
      rpmDrop: baselineRpm - medianForWindow(segment, "rpm"), status: "inconclusive", confidence: "Low",
      confidenceReasons: ["EGT coverage is unavailable; this is an RPM-only candidate."],
    }));
  }
  return candidates.filter((check) => check.mode === "singleMagDrop").map((check) => ({
    eventType: "mag-check", startTime: check.startTime, endTime: check.endTime, durationSeconds: check.duration,
    phase: check.phase, severity: "info", rpmBaseline: check.baselineRpm, rpmMin: check.rpmMin, rpmDrop: check.rpmDrop,
    status: check.responseStatus, confidence: check.confidence, confidenceReasons: check.confidenceReasons,
    recovery: check.recovery, sequenceIndex: check.sequenceIndex, checkIndex: check.checkIndex,
  }));
}

export function analyzeMagCheck(data, options = {}) {
  const descriptors = Array.isArray(options.engineDescriptors) ? options.engineDescriptors : null;
  if (descriptors && descriptors.length > 1) {
    const perEngine = descriptors.map((engine) => {
      const egtSourceKeys = engineCylinderKeys(engine, "egt");
      const projected = projectFlightDataForEngine(data, engine);
      const result = labelEngineMetricResult(analyzeMagCheckForKeys(projected, egtSourceKeys), descriptors);
      return {
        engineKey: engine.engineKey ?? engine.id ?? `engine${(engine.index ?? 0) + 1}`,
        label: engine.label,
        shortLabel: engine.shortLabel ?? engine.label,
        result,
      };
    }).filter(Boolean);
    const scoredEngines = perEngine.filter((entry) => Number.isFinite(entry.result.score));
    if (scoredEngines.length < 2) return combineEngineMetricResults(perEngine);
    if (scoredEngines.length >= 2) {
      const worst = scoredEngines.reduce((acc, entry) => acc === null || entry.result.score < acc.result.score ? entry : acc, null);
      const score = scoredEngines.reduce((acc, entry) => Math.min(acc, entry.result.score), 100);
      const note = `${worst.shortLabel ? `${worst.shortLabel}: ` : ""}${worst.result.note}`;
      return {
        key: "mag",
        score,
        note,
        details: {
          ...worst.result.details,
          engines: perEngine.map((entry) => ({
            engineKey: entry.engineKey,
            label: entry.label,
            shortLabel: entry.shortLabel,
            score: entry.result.score,
            note: entry.result.note,
            details: entry.result.details,
          })),
          worstEngineKey: worst.engineKey,
        },
      };
    }
  }
  return analyzeMagCheckForKeys(data, cylinderKeys("egt"));
}
